import React, {useState, useContext, useEffect} from 'react'
import LongTermFinancialResults from '../components/LongTermFinancialResults';
import LongTermFinancialResultsGraph from '../components/LongTermFInancialResultsGraph';
import { currencyToSymbol } from '../../utils/commonUtils';
import { UserSettingsContext, getDescriptionBySettingName } from "../../../context/UserSettingsContext";
import { useDashboardContext } from '../../../context/DashboardContext';
import { fetchFinancialResults, fetchQuarterlyFinancialData } from "../../api/api_calls";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom"

const FinancialDataTab = ({ secCode }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams() 
    const { state: dashboardState, dispatch: dashboardDispatch } = useDashboardContext();
    const [showLongTermGraph, setShowLongTermGraph] = useState(false);
    const [selectedTimeRange, setSelectedTimeRange] = useState('5Y');
    const [labelState, setLabelState] = useState('auto');
    const [graphDataAnnual, setGraphDataAnnual] = useState(null);
    const [graphDataQuarter, setGraphDataQuarter] = useState(null);
    const [selectedPeriod, setSelectedPeriod] = useState(null);
    const [selectedAccounts, setSelectedAccounts] = useState([]);
    const { selectableAccounts } = useContext(UserSettingsContext);
    const { userSettings, addUserSetting } = useContext(UserSettingsContext);
    const [financialData, setFinancialData] = useState(null);
    const [quarterlyFinancialData, setQuarterlyFinancialData] = useState(null);
    const [isHorizontal, setIsHorizontal] = useState(true);

    useEffect(() => {
        const columns = searchParams.get("columns")
        const year = searchParams.get("year")
        const annualQuarter = searchParams.get("annualQuarter")

        const account = getDescriptionBySettingName(userSettings, 'dashboard_financial_data_graph');
        if (selectableAccounts) {
            if (columns) {
                const columnList = columns.split(",")
                const accountDefinitions = columnList
                    .map((col) => {
                        return selectableAccounts.find((acc) => acc.col === col) || null
                    })
                    .filter(Boolean)
                if (accountDefinitions.length > 0) {
                    setSelectedAccounts(accountDefinitions)
                }
            } else if (account) {
                const accountDefinition = selectableAccounts.find((acc) => (acc.col === account))
                setSelectedAccounts(accountDefinition);
            } else {
                const defaultAccount = [selectableAccounts.find((acc) => acc.col === 'sales')];
                setSelectedAccounts(defaultAccount);
            }
        }

        const timeRange = getDescriptionBySettingName(userSettings, 'dashboard_financial_data_graph_timerange');
        if (year) {
            setSelectedTimeRange(year)
        } else if (timeRange) {
            setSelectedTimeRange(timeRange)
        } else {
            setSelectedTimeRange('5Y')
        }
        
        if (annualQuarter) {
            setSelectedPeriod(annualQuarter); // URLの値を優先
        } else {
            const period = getDescriptionBySettingName(userSettings, 'dashboard_financial_data_graph_period');
            setSelectedPeriod(period || 'annual'); // URLに指定がなければ設定を適用
        }
        const label = getDescriptionBySettingName(userSettings, 'dashboard_financial_data_graph_label');
        if (label) {
            setLabelState(label)
        } else {
            setLabelState('auto')
        }
    }, [selectableAccounts])

    useEffect(() => {
        if (selectedAccounts) {
            setSearchParams((prevParams) => {
                const newParams = new URLSearchParams(prevParams);
                const existingColumns = newParams.get("columns");
    
                const columnsString = selectedAccounts
                    .map((account) => account.col)
                    .join(",");
    
                // 既に存在する columns と異なる場合のみ更新
                if (existingColumns !== columnsString) {
                    newParams.set("columns", columnsString);
                }
    
                return newParams;
            });
    
            selectedAccounts.forEach((acc) => updateGraph(acc.col, acc.col_name, acc.unit));
        }
    }, [selectedAccounts, setSearchParams, financialData, quarterlyFinancialData]);

    
    useEffect(() => {
        if (selectedPeriod) {
            setSearchParams((prevParams) => {
                const newParams = new URLSearchParams(prevParams)
                if (selectedPeriod === 'annual') {
                    newParams.set("annualQuarter", 'annual')
                } else {
                    newParams.set("annualQuarter", 'quarterly')
                }
                return newParams
            })
        }
    }, [selectedPeriod, setSearchParams])
    
    const getFinancialData = async () => {
        // 降順 
        if (dashboardState.financialData) {
            setFinancialData(dashboardState.financialData);
        } else {
            const fetchedData = await fetchFinancialResults(secCode, navigate, location);
            const finData = fetchedData.data;
            dashboardDispatch({ type: 'SET_FINANCIAL_DATA', payload: finData })
            setFinancialData(finData);
        }

        if (selectedPeriod === 'quarterly') {
            if (dashboardState.financialDataQuarter) {
                setFinancialData(dashboardState.financialData);
            } else {
                const fetchedData = await fetchQuarterlyFinancialData(secCode, null, navigate, location);
                const finData = fetchedData.data;
                dashboardDispatch({ type: 'SET_FINANCIAL_DATA_QUARTER', payload: finData })
                setQuarterlyFinancialData(finData)
            }
        }
    }

    useEffect(() => {
        getFinancialData();
    }, [secCode, selectedPeriod])

    useEffect(() => {
        const data = createAnnualGraphData(selectedAccounts, financialData)
        setGraphDataAnnual(data)
    }, [financialData, selectedAccounts])
    
    useEffect(() => {
        const data = createQuarterlyGraphData(selectedAccounts, quarterlyFinancialData)
        setGraphDataQuarter(data)
    }, [quarterlyFinancialData, selectedAccounts])
    
    const makeGraphDataAnnual = (key, label, unit, graphType) => {
        const x = financialData.map((data) => data.fiscal_year);
        const y = financialData.map((data) => data[key]);
        
        const data = {
            key: key,  // keyを識別用に追加
            graphType: graphType,
            x: x,
            y: y,
            x_unit: '年度',
            y_unit: unit === 'value' ? currencyToSymbol(financialData[0]?.reported_currency) : unit,
            name: label,
        };
        return data;
    };

    const makeGraphDataQuarter = (key, label, unit, graphType) => {
        const x = quarterlyFinancialData.map((data) => data.fiscal_year + ' ' + data.type_of_period);
        const y = quarterlyFinancialData.map((data) => data[key]);
        const data = {
            key: key,  // keyを識別用に追加
            graphType: graphType,
            x: x,
            y: y,
            x_unit: '年度',
            y_unit: unit === 'value' ? currencyToSymbol(quarterlyFinancialData[0]?.reported_currency) : unit,
            name: label,
        };
        return data;
    };

    const updateGraphAnnual = (key, label, unit) => {
        const graphType = unit === '%' ? 'line' : 'bar';
        if (!financialData) {
            return 
        }

        if (graphDataAnnual === null) {
            setGraphDataAnnual([])
        }

        setGraphDataAnnual((prevData) => {
            const exists = prevData.some((series) => series.key === key);
            // 既存のキーがある場合はそのまま、ない場合は追加
            if (exists) {
                return prevData; // キーが存在する場合はそのまま保持
            } else { // 追加
                return [...prevData.filter((series) => series.key !== key), makeGraphDataAnnual(key, label, unit, graphType)];
            }
        });
        
    }
    
    const updateGraphQuarter = async(key, label, unit) => {
        const graphType = unit === '%' ? 'line' : 'bar';
        if (!quarterlyFinancialData) {
            return 
        }

        if (graphDataQuarter === null) {
            setGraphDataQuarter([])
        }
        setGraphDataQuarter((prevData) => {
            const exists = prevData.some((series) => series.key === key);
            
            // 既存のキーがある場合は除外、ない場合は追加
            if (exists) {
                return prevData; // キーが存在する場合はそのまま保持
            } else { // 追加
                return [...prevData.filter((series) => series.key !== key), makeGraphDataQuarter(key, label, unit, graphType)];
            }
        });
        
    }

    
    useEffect(() => {
        if (selectedPeriod === 'annual' && graphDataAnnual && graphDataAnnual.length > 0) {
            setShowLongTermGraph(true);
        } else if (selectedPeriod === 'quarterly' && graphDataQuarter && graphDataQuarter.length > 0) {
            setShowLongTermGraph(true);
        } else {
            setShowLongTermGraph(false)
        }
    }, [selectedPeriod, graphDataAnnual, graphDataQuarter])

    const updateGraph = (key, label, unit) => {
        if (selectedPeriod === 'annual') {
            updateGraphAnnual(key, label, unit)
        } else {
            updateGraphQuarter(key, label, unit)
        }
    };

    const toDate = (date) => {
        if (date) {
            const year = date.slice(0, 4)
            const month = parseInt(date.slice(5, 7), 10);
            return `${year}/${month}`
        } else {
            return ""
        }
    }
    
    const createAnnualGraphData = (selectedAccounts, financialData) => {
        if (!financialData || financialData.length === 0) return [];

        return selectedAccounts.map(account => {
            const key = account.col;
            const label = account.col_name;
            const unit = account.unit;
            const graphType = unit === '%' ? 'line' : 'bar';

            const x = financialData.map(data => toDate(data.fiscal_date));
            const y = financialData.map(data => data[key]);

            return {
                key: key,
                graphType: graphType,
                x: x,
                y: y,
                x_unit: '',
                y_unit: unit === 'value' ? currencyToSymbol(financialData[0]?.reported_currency) : unit,
                name: label
            };
        });
    };
    
    const createQuarterlyGraphData = (selectedAccounts, quarterlyFinancialData) => {
        if (!quarterlyFinancialData || quarterlyFinancialData.length === 0) return [];

        return selectedAccounts.map(account => {
            const key = account.col;
            const label = account.col_name;
            const unit = account.unit;
            const graphType = unit === '%' ? 'line' : 'bar';

            const x = quarterlyFinancialData.map(data => data.fiscal_year + ' ' + data.type_of_period);
            const y = quarterlyFinancialData.map(data => data[key]);

            return {
                key: key,
                graphType: graphType,
                x: x,
                y: y,
                x_unit: '年度',
                y_unit: unit === 'value' ? currencyToSymbol(quarterlyFinancialData[0]?.reported_currency) : unit,
                name: label
            };
        });
    };

    const onPeriodChange = (period) => {
        // if (period === 'annual') {
        //     setGraphDataAnnual(null)
        // } else {
        //     setGraphDataQuarter(null)
        // }
        setSelectedPeriod(period);
    }

    const updateSelectedAccounts = (accountColumn) => {
        setSelectedAccounts((prev) => {
            // 既に選択されているかどうかをチェック
            const isSelected = prev.some((account) => account.col === accountColumn);
    
            if (isSelected) {
                // 既に選択されている場合は削除
                return prev.filter((account) => account.col !== accountColumn);
            } else {
                // 選択されていない場合は追加
                const newAccount = selectableAccounts.find((account) => account.col === accountColumn);
                return [...prev, newAccount];
            }
        });
    };
    
    const onSaveClick = async () => {
        let newSettings = [
                // {
                //     setting_name: "dashboard_financial_data_graph",
                //     description: selectedAccounts.col,
                // },
                {
                    setting_name: "dashboard_financial_data_graph_timerange",
                    description: selectedTimeRange,
                },
                {
                    setting_name: "dashboard_financial_data_graph_period",
                    description: selectedPeriod,
                },
                {
                    setting_name: "dashboard_financial_data_graph_label",
                    description: labelState,
                }
            ]
            for (let i = 0; i < newSettings.length; i++) {
                await addUserSetting(newSettings[i]);
            }
    }

    return (
        <div className="tabcontent">
            {(selectedPeriod === 'annual' && financialData)  ?
                <LongTermFinancialResults 
                    financialData={financialData}  
                    updateSelectedAccounts={updateSelectedAccounts} 
                    selectedAccounts={selectedAccounts}
                    selectedPeriod={selectedPeriod}
                    handlePeriodChange={onPeriodChange}
                    isHorizontal={isHorizontal}
                    setIsHorizontal={setIsHorizontal}
                />
                : 
                (selectedPeriod === 'quarterly' && quarterlyFinancialData)  ?
                <LongTermFinancialResults 
                    financialData={quarterlyFinancialData}  
                    updateSelectedAccounts={updateSelectedAccounts} 
                    selectedAccounts={selectedAccounts}
                    selectedPeriod={selectedPeriod}
                    handlePeriodChange={onPeriodChange}
                    isHorizontal={isHorizontal}
                    setIsHorizontal={setIsHorizontal}
                />
                : null
                            
            }
            {showLongTermGraph &&  
                <LongTermFinancialResultsGraph 
                    graphDataAnnual={graphDataAnnual}
                    graphDataQuarter={graphDataQuarter}
                    accounts={selectedAccounts} 
                    unit='value' 
                    labelState={labelState} 
                    setLabelState={setLabelState} 
                    selectedTimeRange={selectedTimeRange} 
                    setSelectedTimeRange={setSelectedTimeRange} 
                    selectedPeriod={selectedPeriod} 
                    onSaveClick={onSaveClick} />
            }
        </div>
    );
};

export default FinancialDataTab;